import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DatePicker from 'react-datepicker';
import {
  FileCsv,
  FilePdf,
  CalendarBlank,
  FileText,
  ChartBar,
  TelegramLogo,
  SpinnerGap,
} from '@phosphor-icons/react';
import FYClosingReport from './FYClosingReport';
import QuarterlyReport from './QuarterlyReport';
import { BACKEND_URL } from '../config';

const ReportsTab = () => {
  const [pdfStartDate, setPdfStartDate] = useState(null);
  const [pdfEndDate, setPdfEndDate] = useState(null);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  const handleExportCSV = async () => {
    setExportingCSV(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sales_report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (err) { console.error(err.message); toast.error('Failed to export CSV'); }
    finally { setExportingCSV(false); }
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const params = {};
      if (pdfStartDate) params.start_date = pdfStartDate.toISOString();
      if (pdfEndDate) params.end_date = pdfEndDate.toISOString();
      const response = await axios.get(`${BACKEND_URL}/api/export/pdf`, { params, withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'analytics_report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(pdfStartDate || pdfEndDate ? 'Date-range PDF exported' : 'PDF exported successfully');
    } catch (err) { console.error(err.message); toast.error('Failed to export PDF'); }
    finally { setExportingPDF(false); }
  };

  return (
    <div className="space-y-8" data-testid="reports-tab-content">
      {/* Quick Exports */}
      <div className="bg-card border border-border rounded-sm p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-9 h-9 rounded-sm bg-primary/10 flex items-center justify-center">
            <FileText size={20} weight="regular" className="text-primary" />
          </div>
          <div>
            <div className="text-base font-semibold tracking-tight">Quick Exports</div>
            <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground">SALES DATA & ANALYTICS</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* CSV Export */}
          <div className="border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileCsv size={20} weight="regular" className="text-green-600" />
              <div className="text-sm font-semibold">Sales Data (CSV)</div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Export all sales transactions as a CSV file. Includes client name, amount, date, and description.
            </p>
            <Button
              onClick={handleExportCSV}
              disabled={exportingCSV}
              variant="outline"
              className="w-full rounded-sm"
              data-testid="reports-export-csv"
            >
              {exportingCSV ? (
                <><SpinnerGap size={16} className="mr-2 animate-spin" />Exporting...</>
              ) : (
                <><FileCsv size={16} className="mr-2" />Download CSV</>
              )}
            </Button>
          </div>

          {/* PDF Export */}
          <div className="border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <FilePdf size={20} weight="regular" className="text-red-600" />
              <div className="text-sm font-semibold">Analytics Report (PDF)</div>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Generate a PDF report with revenue summary, top clients, and monthly breakdown. Optionally filter by date range.
            </p>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-1.5 bg-background border border-border rounded-sm px-2.5 py-1.5 flex-1">
                <CalendarBlank size={14} className="text-muted-foreground" />
                <DatePicker
                  selected={pdfStartDate}
                  onChange={(d) => setPdfStartDate(d)}
                  selectsStart
                  startDate={pdfStartDate}
                  endDate={pdfEndDate}
                  placeholderText="Start"
                  className="text-xs w-20 outline-none bg-transparent"
                  data-testid="reports-pdf-start-date"
                />
              </div>
              <span className="text-xs text-muted-foreground">to</span>
              <div className="flex items-center gap-1.5 bg-background border border-border rounded-sm px-2.5 py-1.5 flex-1">
                <CalendarBlank size={14} className="text-muted-foreground" />
                <DatePicker
                  selected={pdfEndDate}
                  onChange={(d) => setPdfEndDate(d)}
                  selectsEnd
                  startDate={pdfStartDate}
                  endDate={pdfEndDate}
                  minDate={pdfStartDate}
                  placeholderText="End"
                  className="text-xs w-20 outline-none bg-transparent"
                  data-testid="reports-pdf-end-date"
                />
              </div>
              {(pdfStartDate || pdfEndDate) && (
                <button onClick={() => { setPdfStartDate(null); setPdfEndDate(null); }} className="text-xs text-muted-foreground hover:text-foreground">
                  Clear
                </button>
              )}
            </div>
            <Button
              onClick={handleExportPDF}
              disabled={exportingPDF}
              variant="outline"
              className="w-full rounded-sm"
              data-testid="reports-export-pdf"
            >
              {exportingPDF ? (
                <><SpinnerGap size={16} className="mr-2 animate-spin" />Generating...</>
              ) : (
                <><FilePdf size={16} className="mr-2" />{pdfStartDate || pdfEndDate ? 'Download Date Range PDF' : 'Download PDF'}</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Quarterly Reports */}
      <div data-testid="reports-quarterly-section">
        <QuarterlyReport />
      </div>

      {/* FY Closing Reports */}
      <div data-testid="reports-fy-section">
        <FYClosingReport />
      </div>
    </div>
  );
};

export default ReportsTab;
